import * as am5xy from '@amcharts/amcharts5/xy';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
    Component,
    DestroyRef,
    ElementRef,
    Inject,
    InputSignal,
    NgZone,
    PLATFORM_ID,
    Signal,
    WritableSignal,
    computed,
    effect,
    inject,
    input,
    signal,
    untracked,
    viewChild
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { isEmpty, isNil, slice } from 'ramda';
import { EquipmentTypes, Rate, RateInfo, Timeframes } from '../models';
import { IqNtlGraphRatesStateService, NtlGraphBuilderService } from '../state';
import { IqEventBusBrokerService } from '@dat/iq-event-bus';

@Component({
    selector: 'iq-ntl-graph-rates-multi',
    standalone: true,
    imports: [CommonModule, MatIconModule],
    templateUrl: './iq-ntl-graph-rates-multi.component.html',
    styleUrl: './iq-ntl-graph-rates-multi.component.scss',
})
export class IqNtlGraphRatesMultiComponent {
    id: InputSignal<string> = input.required();

    timeframe: WritableSignal<Timeframes> = signal(Timeframes.OneYear);
    equipmentType: WritableSignal<EquipmentTypes> = signal(EquipmentTypes.Van);

    private _bus: IqEventBusBrokerService = inject(IqEventBusBrokerService);
    private _destroyRef: DestroyRef = inject(DestroyRef);
    private _state: IqNtlGraphRatesStateService = inject(IqNtlGraphRatesStateService);

    /**
     * Rates for the currently selected timeframe & equipment
     *
     * (orr.epstein)
     */
    // rates: Signal<Rate[]> = computed(() => {
    //     return this.cachedRates()[this.equipmentType() ?? EquipmentTypes.Van][this.timeframe() ?? Timeframes.OneYear];
    // });
    rateInfo: Signal<RateInfo> = computed(() => {
        return this._state.rates()[this.equipmentType()];
    });

    rates: Signal<Rate[]> = computed(() => {
        return this.rateInfo().rates;
    });

    errored: Signal<boolean> = computed(() => {
        return this.rateInfo().errored;
    });

    /**
     * If there are rates for the currently selected timeframe & equipment.
     *
     * (orr.epstein)
     */
    hasRates: Signal<boolean> = computed(() => {
        return !isNil(this.equipmentType()) &&
            !isEmpty(this.rates());
    });

    loading: Signal<boolean> = computed(() => {
        return !this.hasRates() && !this.errored();
    });

    graph: Signal<ElementRef> = viewChild<ElementRef>('graph');

    chart: Signal<am5xy.XYChart> = computed(() => {
        const graphElement: HTMLElement = this.graph()?.nativeElement;

        return !isNil(graphElement)
            ? this._graph.buildGraph(graphElement)
            : undefined;
    });

    constructor(
        @Inject(PLATFORM_ID) private _platformId: Object,
        private _zone: NgZone,
        private _graph: NtlGraphBuilderService,
    ) {
        effect(() => {
            const id = this.id();

            untracked(() => {
                const hanlders = this._bus.registerMap({
                    setNationalRatesGraphTimeframe: {
                        callback: (payload: any) => {
                            if (payload.id !== id) return;

                            this.timeframe.set(payload.timeframe);
                        },
                    },
                    setLandingEquipmentType: {
                        callback: (payload: any) => {
                            this.equipmentType.set(payload.equipmentType);
                        }
                    }
                });

                this._destroyRef.onDestroy(() => {
                    this._bus.unregisterMap(hanlders);
                });
            });
        });

        /**
         * this effect runs when we recieve rates for the timeframe & equipment
         * and does a couple different things to the graph (if it exists):
         *      1. sets the y-axis range on the graph so it can display all data
         *         points
         *      2. sets the data on each series
         *
         * (orr.epstein)
         */
        effect(() => {
            const chart = this.chart();

            if (!isNil(chart) && this.hasRates()) {
                const sliceAmt: number = {
                    [Timeframes.OneYear]: -12,
                    [Timeframes.SixMonths]: -6,
                    [Timeframes.ThreeMonths]: -3
                }[this.timeframe()];
                const rates = slice(sliceAmt, Infinity, this.rates());

                this._graph.setYAxisRange(
                    chart,
                    rates
                );

                this._graph.setData(
                    chart,
                    rates
                );
            }
        });

        this._destroyRef.onDestroy(() => {
            this._browserOnly(() => {
                this.chart()?.dispose();
            });
        });
    }

    private _browserOnly(fn: () => void): void {
        if (isPlatformBrowser(this._platformId)) {
            this._zone.runOutsideAngular(fn);
        }
    }
}
